<template>
	<div class="editor-box" :class="{ 'editor-disabled': disabled }">
		<vue-ueditor-wrap
			ref="editorWrap"
			v-model="editorContent"
			:editor-id="editorId"
			:config="mergedConfig"
			:editor-dependencies="editorDependencies"
			@ready="onReady"
			@before-init="onBeforeInit"
		/>
	</div>
</template>

<script>
	import { ueditorHandleUrl } from '@/api/mall'
	import VueUeditorWrap from 'vue-ueditor-wrap'

	/**
	 * @description UEditorPlus 富文本编辑器封装组件
	 * @example
	 *   <UEditor v-model="content" :height="400" :disabled="false" />
	 */

	let globalId = 0

	export default {
		name: 'UEditor',
		components: {
			VueUeditorWrap
		},
		props: {
			// 编辑器内容，支持 v-model
			value: {
				type: String,
				default: ''
			},
			// 编辑器高度（不含工具栏）
			height: {
				type: [Number, String],
				default: 500
			},
			// 编辑器最小高度
			minHeight: {
				type: [Number, String],
				default: 300
			},
			// 编辑器宽度
			width: {
				type: [Number, String],
				default: '100%'
			},
			// 是否禁用
			disabled: {
				type: Boolean,
				default: false
			},
			// 后端服务地址（图片/文件上传等）
			serverUrl: {
				type: String,
				default: ''
			},
			// 自定义工具栏，传 null 则使用默认
			toolbars: {
				type: Array,
				default: () => null
			},
			// 占位文本
			placeholder: {
				type: String,
				default: ''
			},
			// 是否自动清空初始内容中的空格
			autoClearEmptyNode: {
				type: Boolean,
				default: true
			},
			// 是否自动长高
			autoHeightEnabled: {
				type: Boolean,
				default: false
			},
			// 是否开启字数统计
			wordCount: {
				type: Boolean,
				default: true
			},
			// 最多允许字数
			maximumWords: {
				type: Number,
				default: 100000
			},
			// 自定义 z-index
			zIndex: {
				type: Number,
				default: 99999
			},
			// 额外的 UEditor 配置，会合并到默认配置中
			extraConfig: {
				type: Object,
				default: () => ({})
			}
		},
		data() {
			return {
				editorContent: this.value,
				editorId: `ueditor-${++globalId}-${Date.now()}`,
				editorInstance: null,
				editorDependencies: ['ueditor.config.js', 'ueditor.all.js']
			}
		},
		computed: {
			mergedConfig() {
				const config = {
					// 编辑器资源文件根路径
					UEDITOR_HOME_URL: '/static/UEditorPlus/',
					UEDITOR_CORS_URL: '/static/UEditorPlus/',

					// 尺寸
					initialFrameHeight: Number(this.height),
					initialFrameWidth: typeof this.width === 'number' ? `${this.width}px` : this.width,
					minFrameHeight: Number(this.minHeight),

					// 自动长高
					autoHeightEnabled: this.autoHeightEnabled,

					// 字数统计
					wordCount: this.wordCount,
					maximumWords: this.maximumWords,

					// z-index
					zIndex: this.zIndex,

					// 占位文本（UEditorPlus 支持）
					placeholder: this.placeholder,

					// 自动清空空节点
					autoClearEmptyNode: this.autoClearEmptyNode,

					// 后端服务地址
					serverUrl: this.serverUrl || ueditorHandleUrl(),

					// 聚焦时清除初始内容
					autoClearinitialContent: false,

					// 允许粘贴图片
					catchRemoteImageEnable: true,

					// 工具栏（默认使用 UEditor 自带，也可自定义）
					...(this.toolbars ? { toolbars: this.toolbars } : {}),

					// 额外配置合并
					...this.extraConfig
				}
				return config
			}
		},
		watch: {
			// 监听外部 value 变化，同步到编辑器
			value(newVal) {
				if (newVal !== this.editorContent) {
					this.editorContent = newVal
				}
			},
			// 监听内部内容变化，通知父组件
			editorContent(newVal) {
				this.$emit('input', newVal)
				this.$emit('change', newVal)
			},
			// 监听禁用状态
			disabled(val) {
				this.$nextTick(() => {
					this.setDisabled(val)
				})
			}
		},
		methods: {
			/**
			 * 编辑器初始化完成回调
			 * @param {Object} editor - UEditor 实例
			 */
			onReady(editor) {
				this.editorInstance = editor

				// 应用初始禁用/只读状态
				this.setDisabled(this.disabled)

				// 绑定编辑器原生事件
				editor.addListener('contentChange', () => {
					this.$emit('contentChange', editor.getContent())
				})

				editor.addListener('blur', () => {
					this.$emit('blur', editor.getContent())
				})

				editor.addListener('focus', () => {
					this.$emit('focus')
				})

				this.$emit('ready', editor)
			},

			/**
			 * 编辑器初始化前回调
			 */
			onBeforeInit(editorId) {
				this.$emit('before-init', editorId)
			},

			/**
			 * 设置禁用状态
			 */
			setDisabled(disabled) {
				if (!this.editorInstance) return
				if (disabled) {
					this.editorInstance.setDisabled()
				} else {
					this.editorInstance.setEnabled()
				}
			},

			// =============== 以下方法供父组件通过 ref 调用 ===============

			/**
			 * 获取编辑器内容（HTML）
			 * @returns {String}
			 */
			getContent() {
				return this.editorInstance ? this.editorInstance.getContent() : ''
			},

			/**
			 * 获取纯文本内容
			 * @returns {String}
			 */
			getPlainTxt() {
				return this.editorInstance ? this.editorInstance.getPlainTxt() : ''
			},

			/**
			 * 获取带格式的文本内容
			 * @returns {String}
			 */
			getContentTxt() {
				return this.editorInstance ? this.editorInstance.getContentTxt() : ''
			},

			/**
			 * 设置编辑器内容
			 * @param {String} html
			 * @param {Boolean} isAppendTo - 是否追加
			 */
			setContent(html, isAppendTo = false) {
				if (this.editorInstance) {
					this.editorInstance.setContent(html, isAppendTo)
				}
			},

			/**
			 * 插入 HTML
			 * @param {String} html
			 */
			insertHtml(html) {
				if (this.editorInstance) {
					this.editorInstance.execCommand('inserthtml', html)
				}
			},

			/**
			 * 获取内容长度（纯文本）
			 * @returns {Number}
			 */
			getContentLength() {
				return this.editorInstance ? this.editorInstance.getContentLength(true) : 0
			},

			/**
			 * 判断是否为空内容
			 * @returns {Boolean}
			 */
			isEmpty() {
				return this.editorInstance
					? this.editorInstance.getContent() === '<p><br/></p>' || this.editorInstance.getContent() === ''
					: true
			},

			/**
			 * 清空内容
			 */
			clearContent() {
				if (this.editorInstance) {
					this.editorInstance.setContent('')
				}
			},

			/**
			 * 使编辑器获得焦点
			 */
			focus() {
				if (this.editorInstance) {
					this.editorInstance.focus()
				}
			},

			/**
			 * 获取编辑器实例（用于调用更多原生 API）
			 * @returns {Object}
			 */
			getEditorInstance() {
				return this.editorInstance
			}
		}
	}
</script>

<style lang="scss" scoped>
	.editor-box {
		width: 100%;
		position: relative;
		z-index: 99;

		::v-deep .edui-editor {
			border: 1px solid #dcdfe6;
			border-radius: 4px;
		}

		::v-deep .edui-editor-toolbarbox {
			border-bottom: 1px solid #e4e7ed;
		}

		::v-deep .edui-editor-iframeholder {
			overflow: hidden;
		}
	}

	.editor-disabled {
		::v-deep .edui-editor {
			pointer-events: none;
			cursor: not-allowed;
			background-color: #f5f7fa;
			opacity: 0.8;
		}
	}
</style>
